import React, { useState } from "react";
import { toast } from "sonner";
import { X, Camera, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CUISINES = ["Biryani", "Pizza", "Chinese", "South Indian", "North Indian", "Desserts", "Healthy"];
const DIETARY = ["Jain", "Vegan", "Gluten-free", "Low-carb"];

export function EditProfileSheet({ open, onClose }) {
  const { user, patchUser } = useAuth();
  const [f, setF] = useState(() => ({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    dob: user?.dob || "", gender: user?.gender || "", language: user?.language || "English",
    veg_pref: user?.veg_pref || "both", spice: user?.spice || "Medium",
    cuisines: user?.cuisines || [], dietary: user?.dietary || [], photo: user?.photo || "",
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Image must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => set("photo", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const { data } = await api.put("/profile", f);
      patchUser(data);
      toast.success("Profile updated");
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-w-[520px] mx-auto rounded-t-3xl p-0 max-h-[92vh] overflow-y-auto" data-testid="edit-profile-sheet">
        <SheetTitle className="sr-only">Edit profile</SheetTitle>
        <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">Edit Profile</h3>
          <button onClick={onClose} data-testid="edit-profile-close"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-coral-light overflow-hidden flex items-center justify-center">
                {f.photo ? <img src={f.photo} alt="" className="w-full h-full object-cover" /> : <span className="text-coral font-bold text-xl">{f.name?.[0] || "B"}</span>}
              </div>
              <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-coral text-white rounded-full flex items-center justify-center cursor-pointer" data-testid="photo-upload">
                <Camera size={14} /><input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
              </label>
            </div>
            {f.photo && <button onClick={() => set("photo", "")} className="text-xs text-bad font-semibold" data-testid="photo-remove">Remove</button>}
          </div>

          <Field label="Full name"><input value={f.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="edit-name" /></Field>
          <Field label="Mobile number"><input value={f.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className="inp" data-testid="edit-phone" /></Field>
          <Field label="Email"><input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="inp" data-testid="edit-email" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth"><input type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} className="inp" data-testid="edit-dob" /></Field>
            <Field label="Gender"><select value={f.gender} onChange={(e) => set("gender", e.target.value)} className="inp" data-testid="edit-gender"><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred language"><select value={f.language} onChange={(e) => set("language", e.target.value)} className="inp"><option>English</option><option>Telugu</option><option>Hindi</option></select></Field>
            <Field label="Food preference"><select value={f.veg_pref} onChange={(e) => set("veg_pref", e.target.value)} className="inp" data-testid="edit-veg"><option value="both">Veg & Non-Veg</option><option value="veg">Veg only</option><option value="nonveg">Non-Veg</option></select></Field>
          </div>
          <Field label="Spice preference"><div className="flex gap-2 flex-wrap">{["Mild", "Medium", "Spicy", "Extra Spicy"].map((s) => <button key={s} onClick={() => set("spice", s)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${f.spice === s ? "bg-coral text-white border-coral" : "bg-white border-border"}`}>{s}</button>)}</div></Field>
          <Field label="Favorite cuisines"><div className="flex gap-2 flex-wrap">{CUISINES.map((c) => <button key={c} onClick={() => toggle("cuisines", c)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${f.cuisines.includes(c) ? "bg-coral text-white border-coral" : "bg-white border-border"}`}>{c}</button>)}</div></Field>
          <Field label="Dietary preferences"><div className="flex gap-2 flex-wrap">{DIETARY.map((c) => <button key={c} onClick={() => toggle("dietary", c)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${f.dietary.includes(c) ? "bg-veg text-white border-veg" : "bg-white border-border"}`}>{c}</button>)}</div></Field>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-border p-4 flex gap-2">
          <button onClick={onClose} className="flex-1 border border-border rounded-xl py-3 font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="flex-1 bg-coral text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 tap" data-testid="save-profile">{busy ? <Loader2 size={17} className="animate-spin" /> : "Save Changes"}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-ink-600 mb-1 block">{label}</label>{children}</div>;
}
